import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { Save, Loader2, Globe, Search, Type, Tags } from 'lucide-react';
import { SEOSettings } from '@/hooks/useSEO';

export default function SEOSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [seo, setSeo] = useState<SEOSettings>({
    title: '',
    description: '',
    keywords: '',
  });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'seo'), (docSnap) => {
      if (docSnap.exists()) {
        setSeo(docSnap.data() as SEOSettings);
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);

    try {
      await setDoc(doc(db, 'settings', 'seo'), {
        ...seo,
        updatedAt: new Date().toISOString()
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving SEO info:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-10">
      <div>
        <h1 className="text-3xl font-black text-white uppercase tracking-tight">SEO Settings</h1>
        <p className="text-white/50 text-sm">Manage how your website appears on Google and social media.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-10">
        
        <section className="space-y-6">
          <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-8">
            
            {/* Title */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white">
                <Type size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">Site Title</h3>
              </div>
              <p className="text-white/40 text-xs">This is the title that appears on the browser tab and Google search results. Recommended length: 50-60 characters.</p>
              <input
                type="text"
                value={seo.title}
                onChange={e => setSeo({ ...seo, title: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="Bucara Burger | Colombian Food Truck Boise"
              />
              <div className={`text-xs font-bold text-right ${seo.title.length > 60 ? 'text-red-400' : 'text-white/30'}`}>
                {seo.title.length} / 60
              </div>
            </div>

            <hr className="border-white/5" />

            {/* Description */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white">
                <Search size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">Meta Description</h3>
              </div>
              <p className="text-white/40 text-xs">A short paragraph describing your business. This often appears under the title in search results. Recommended length: 150-160 characters.</p>
              <textarea
                value={seo.description}
                onChange={e => setSeo({ ...seo, description: e.target.value })}
                rows={3}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm resize-none"
                placeholder="Bucara Burger is Boise's favorite Colombian food truck..."
              />
              <div className={`text-xs font-bold text-right ${seo.description.length > 160 ? 'text-red-400' : 'text-white/30'}`}>
                {seo.description.length} / 160
              </div>
            </div>

            <hr className="border-white/5" />

            {/* Keywords */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white">
                <Tags size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">Meta Keywords</h3>
              </div>
              <p className="text-white/40 text-xs">Comma-separated keywords related to your business (e.g. food truck, colombian food, boise).</p>
              <input
                type="text"
                value={seo.keywords}
                onChange={e => setSeo({ ...seo, keywords: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="Colombian food truck Boise, burgers Boise Idaho..."
              />
            </div>
            
          </div>
        </section>

        {/* Live Preview */}
        <section className="space-y-6">
          <h2 className="text-xs font-black text-[#F57F25] uppercase tracking-[0.2em] border-b border-white/5 pb-2">Google Preview</h2>
          <div className="bg-white p-6 rounded-2xl shadow-sm max-w-2xl font-sans">
            <div className="text-[14px] text-[#202124] mb-1 flex items-center gap-2">
              <Globe size={16} className="text-gray-500" />
              https://bucaraburger.com
            </div>
            <div className="text-[20px] text-[#1a0dab] font-medium leading-tight mb-1 hover:underline cursor-pointer truncate">
              {seo.title || 'Your Site Title Here'}
            </div>
            <div className="text-[14px] text-[#4d5156] leading-[1.58] line-clamp-2">
              {seo.description || 'Your meta description will appear here as a snippet for the searchers to read before clicking the link...'}
            </div>
          </div>
        </section>

        <div className="flex items-center gap-6 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-10 py-5 rounded-3xl transition-all flex items-center justify-center gap-3 uppercase text-sm shadow-xl shadow-[#F57F25]/20 disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : <><Save size={20} /> Save SEO Settings</>}
          </button>
          
          {success && (
            <div className="flex items-center gap-2 text-green-500 font-bold animate-bounce">
              <Globe size={20} />
              <span className="text-xs uppercase tracking-widest">Saved Successfully!</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
